import * as React from 'react';
import { Check } from 'lucide-react';

import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { Input } from '@/hen-ui/atomics/Input';
import { Text } from '@/hen-ui/atomics/Text';
import { cn } from '@/hen-ui/utils';
import { comboboxOptionVariants } from './combobox.variants';

export interface ComboboxOption {
  /** Stable value passed to `onSelect` when the row is chosen. */
  value: string;
  /** Human-readable text rendered in the row and used for filtering. */
  label: string;
  /** Marks the row as non-selectable. */
  disabled?: boolean;
}

export interface ComboboxProps {
  /** Options rendered in the listbox and filtered by the typed text. */
  options: ComboboxOption[];
  /** Values already chosen elsewhere; rendered as selected (checked) rows. */
  selectedValues?: string[];
  /** Fired with the option value when a row is chosen. */
  onSelect: (value: string) => void;
  /** Placeholder shown in the text field. */
  placeholder?: string;
  /** Message shown when the filter matches no options. */
  emptyMessage?: string;
  /** Once `selectedValues` reaches this count, unselected rows are disabled. */
  maxSelectable?: number;
  /** Disables the entire field. */
  disabled?: boolean;
  /** Associates the field with an external `<label htmlFor={id}>`. */
  id?: string;
  /**
   * Accessible name for the text field. Provide this (or wire an external
   * `<label htmlFor={id}>`) whenever the field has no visible label so screen
   * readers announce its purpose (WCAG 4.1.2 / 3.3.2).
   */
  'aria-label'?: string;
  /** Optional class names merged onto the field wrapper. */
  className?: string;
}

const OPTION_ID_PREFIX = 'combobox-option-';

/**
 * Combobox component following HEN Design System standards.
 *
 * An accessible searchable combobox: a text field filters a listbox of options
 * and reports the chosen value via `onSelect`. It follows the WAI-ARIA combobox
 * pattern (`role="combobox"` + `role="listbox"`, `aria-activedescendant`,
 * arrow/Home/End/Enter/Escape keyboard navigation) and marks already-selected
 * rows so callers can layer additive selection on top of their own pills,
 * tiles, or single value. All styling uses tokens from tailwind.config.ts.
 *
 * Deliberately a separate atomic from `MultiSelect`: `MultiSelect` is a closed
 * dropdown-menu checkbox list that owns its selection state, whereas `Combobox`
 * is a type-to-filter search field that only reports the chosen value and lets
 * the caller render its own selection surface (pills, tiles, single value).
 * Folding both behaviours into one component would couple two distinct
 * interaction patterns, so they stay separate.
 *
 * @example
 * // Basic usage
 * <label htmlFor="skills">Add a skill</label>
 * <Combobox
 *   id="skills"
 *   options={[{ value: 'py', label: 'Python' }, { value: 'go', label: 'Go' }]}
 *   onSelect={(value) => addSkill(value)}
 * />
 *
 * @example
 * // Marking selected rows and capping additive selection
 * <Combobox
 *   options={options}
 *   selectedValues={selected}
 *   maxSelectable={3}
 *   onSelect={addSkill}
 * />
 */
export const Combobox = React.forwardRef<HTMLInputElement, ComboboxProps>(
  (
    {
      options,
      selectedValues = [],
      onSelect,
      placeholder = 'Search options...',
      emptyMessage = 'No options found',
      maxSelectable = 0,
      disabled = false,
      id,
      'aria-label': ariaLabel,
      className,
    },
    ref,
  ) => {
    const [searchText, setSearchText] = React.useState('');
    const [showDropdown, setShowDropdown] = React.useState(false);
    const [activeIndex, setActiveIndex] = React.useState(-1);
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const listboxRef = React.useRef<HTMLDivElement>(null);
    const generatedId = React.useId();
    const listboxId = `${generatedId}-listbox`;
    const emptyMessageId = `${generatedId}-empty`;

    const selectedSet = React.useMemo(
      () => new Set(selectedValues),
      [selectedValues],
    );

    const isMaxReached =
      maxSelectable > 0 && selectedValues.length >= maxSelectable;

    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node)
        ) {
          setShowDropdown(false);
          setActiveIndex(-1);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = React.useMemo(() => {
      if (!searchText) return [];
      const normalizedSearchText = searchText.toLowerCase();
      return options.filter((option) =>
        option.label.toLowerCase().includes(normalizedSearchText),
      );
    }, [options, searchText]);

    React.useEffect(() => {
      setActiveIndex(-1);
    }, [filteredOptions]);

    React.useEffect(() => {
      if (activeIndex < 0 || !listboxRef.current) return;
      const activeValue = filteredOptions[activeIndex]?.value;
      if (!activeValue) return;
      const activeElement = document.getElementById(
        `${OPTION_ID_PREFIX}${activeValue}`,
      );
      if (activeElement && typeof activeElement.scrollIntoView === 'function') {
        activeElement.scrollIntoView({ block: 'nearest' });
      }
    }, [activeIndex, filteredOptions]);

    const isSelectable = React.useCallback(
      (option: ComboboxOption) => {
        if (option.disabled) return false;
        const isSelected = selectedSet.has(option.value);
        return isSelected || !isMaxReached;
      },
      [selectedSet, isMaxReached],
    );

    const handleSelect = React.useCallback(
      (option: ComboboxOption) => {
        if (!isSelectable(option)) return;
        onSelect(option.value);
        setSearchText('');
        setShowDropdown(false);
        setActiveIndex(-1);
      },
      [onSelect, isSelectable],
    );

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent) => {
        if (!showDropdown || filteredOptions.length === 0) {
          if (e.key === 'Escape') {
            // Listbox already closed: Escape clears the typed filter
            // (WAI-ARIA combobox pattern).
            setSearchText('');
            setShowDropdown(false);
            setActiveIndex(-1);
          }
          return;
        }

        switch (e.key) {
          case 'ArrowDown': {
            e.preventDefault();
            setActiveIndex((prev) =>
              prev < filteredOptions.length - 1 ? prev + 1 : 0,
            );
            break;
          }
          case 'ArrowUp': {
            e.preventDefault();
            setActiveIndex((prev) =>
              prev > 0 ? prev - 1 : filteredOptions.length - 1,
            );
            break;
          }
          case 'Enter': {
            e.preventDefault();
            if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
              handleSelect(filteredOptions[activeIndex]);
            }
            break;
          }
          case 'Escape': {
            e.preventDefault();
            setShowDropdown(false);
            setActiveIndex(-1);
            break;
          }
          case 'Home': {
            e.preventDefault();
            setActiveIndex(0);
            break;
          }
          case 'End': {
            e.preventDefault();
            setActiveIndex(filteredOptions.length - 1);
            break;
          }
        }
      },
      [showDropdown, filteredOptions, activeIndex, handleSelect],
    );

    const activeDescendant =
      activeIndex >= 0 && filteredOptions[activeIndex]
        ? `${OPTION_ID_PREFIX}${filteredOptions[activeIndex].value}`
        : undefined;

    const isListboxOpen = showDropdown && filteredOptions.length > 0;
    const showEmptyMessage =
      showDropdown && searchText.length > 0 && filteredOptions.length === 0;

    return (
      <div className={cn('relative w-full', className)} ref={wrapperRef}>
        <Input
          ref={ref}
          type='text'
          role='combobox'
          icon='search'
          id={id}
          aria-label={ariaLabel}
          disabled={disabled}
          aria-expanded={isListboxOpen}
          aria-haspopup='listbox'
          aria-controls={isListboxOpen ? listboxId : undefined}
          aria-activedescendant={activeDescendant}
          aria-autocomplete='list'
          placeholder={placeholder}
          value={searchText}
          data-hen-id={COMPONENT_IDS.Combobox}
          onChange={(e) => {
            setSearchText(e.target.value);
            setShowDropdown(e.target.value.length > 0);
          }}
          onFocus={() => searchText.length > 0 && setShowDropdown(true)}
          onKeyDown={handleKeyDown}
        />
        {isListboxOpen && (
          <div
            id={listboxId}
            ref={listboxRef}
            role='listbox'
            className='absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-sm border border-neutral-200 bg-white shadow-sm'
          >
            {filteredOptions.map((option, index) => {
              const isSelected = selectedSet.has(option.value);
              const isActive = index === activeIndex;
              const rowDisabled = !isSelectable(option);

              const state = isSelected
                ? 'selected'
                : rowDisabled
                  ? 'disabled'
                  : isActive
                    ? 'active'
                    : 'default';

              return (
                <button
                  id={`${OPTION_ID_PREFIX}${option.value}`}
                  key={option.value}
                  type='button'
                  role='option'
                  tabIndex={-1}
                  aria-selected={isSelected}
                  disabled={rowDisabled}
                  onClick={() => handleSelect(option)}
                  className={comboboxOptionVariants({ state })}
                >
                  {isSelected && (
                    <Check className='h-4 w-4 shrink-0' aria-hidden />
                  )}
                  <Text variant='b5' tag='span'>
                    {option.label}
                  </Text>
                </button>
              );
            })}
          </div>
        )}
        {showEmptyMessage && (
          <div
            id={emptyMessageId}
            role='status'
            className='absolute z-10 mt-1 w-full rounded-sm border border-neutral-200 bg-white px-xs py-xxs shadow-sm'
          >
            <Text variant='b5' tag='span' color='subtext'>
              {emptyMessage}
            </Text>
          </div>
        )}
      </div>
    );
  },
);

Combobox.displayName = 'Combobox';
